import { getSymmetricKey, isApiError } from "../service/api-actions";

/**
 * Read-only access to the dContacts DEK (cross-app).
 *
 * dContacts owns this key: it mints a 32-byte hex DEK, wraps it to the user's own chain encryption
 * public key, and stores the wrapped blob server-side. dMail only ever READS contacts, so it never
 * mints or wraps — it just unwraps once per session and caches the result in memory.
 *
 * Mirrors dcalendar/src/helper/dekManager.ts getContactsSessionDEK. Two things MUST match that
 * reference exactly or dmail derives a different key string from the same wrapped blob and every
 * contact fails to decrypt:
 *   1. ncog_decrypt is called with { cypherText, version: 'v1' }.
 *   2. The unwrapped key has its NUL padding stripped.
 *
 * The key is deliberately kept in module memory only — never persisted. A DEK written to IndexedDB
 * outlives the session and is a plaintext key at rest.
 */

// Built at runtime rather than written as a regex escape — the wallet NUL-pads the unwrapped key.
const NUL = String.fromCharCode(0);

let _contactsDek = null;
let _contactsDekUser = null;
let _inflight = null;

function stripNulPadding(value) {
  return String(value).split(NUL).join("");
}

async function fetchWrappedContactsDek(userName) {
  const res = await getSymmetricKey({ userName });
  // "The request failed" is not "this user has no DEK". Only the latter may return null, because null
  // sends the caller down the legacy wallet-decrypt path — which quietly fails to open every v2 contact.
  // web3helper catches this throw and falls back on its own, so nothing above needs to change.
  if (isApiError(res)) {
    throw new Error("contactsDek: could not read the dContacts DEK (request failed)");
  }
  const key = res?.data?.encrypted_key;
  return key ? String(key) : null;
}

async function walletUnwrap(wrapped) {
  const res = await window.ncogProvider.request({
    method: "ncog_decrypt",
    params: { cypherText: wrapped, version: "v1" },
  });
  if (!res || res.success === false || !res.response) {
    throw new Error("ncog_decrypt failed to unwrap the dContacts DEK");
  }
  return stripNulPadding(res.response);
}

/**
 * Resolve the dContacts DEK for this session. Unwraps via the wallet at most ONCE per user, then
 * serves every later caller from the module cache. Returns null when the user has no DEK yet
 * (all-legacy contacts) — callers must treat null as "fall back to the wallet decrypt path".
 */
export async function getContactsSessionDEK(userName) {
  const user = userName || localStorage.getItem("UserName");
  if (!user) return null;
  if (_contactsDek && _contactsDekUser === user) return _contactsDek;
  if (_inflight && _inflight.user === user) return _inflight.p;

  const p = (async () => {
    const wrapped = await fetchWrappedContactsDek(user);
    if (!wrapped) return null;
    const dek = await walletUnwrap(wrapped);
    _contactsDek = dek;
    _contactsDekUser = user;
    return dek;
  })();

  _inflight = { user, p };
  try {
    return await p;
  } finally {
    if (_inflight && _inflight.p === p) _inflight = null;
  }
}

/** Normalise a key that a caller already unwrapped, so it matches what dContacts produced. */
export function normalizeDek(value) {
  return value ? stripNulPadding(value) : value;
}

/**
 * Seed the cache with a key the caller already unwrapped, so the rest of the session reuses it
 * instead of prompting the wallet a second time. Sign-in unwraps the DEK for its own validity
 * check — priming here means that one unwrap serves every later reader.
 */
export function primeContactsDek(userName, dek) {
  if (!userName || !dek) return;
  _contactsDek = stripNulPadding(dek);
  _contactsDekUser = userName;
}

/** Drop the cached key. Call on logout and on account switch. */
export function clearContactsDekCache() {
  _contactsDek = null;
  _contactsDekUser = null;
  _inflight = null;
}
